import * as THREE from 'three';
import { DIM_AXIS } from './dimensionTypes';
import { buildAnchorKey, localAxisWorldDir } from './AnchorResolver';
import { projectToScreen } from './ProjectionEngine';

/** Khoảng cách mong muốn (px) từ cạnh vật tới đường DIM khi tạo tự động. */
export const TARGET_OFFSET_PX = 38;
const MIN_OFFSET_M = 0.04;
const MAX_OFFSET_M = 0.6;
/** DIM ngắn hơn ngưỡng này (px) trên màn hình bị phạt (cạnh nằm dọc hướng nhìn). */
const MIN_LENGTH_PX = 40;
/** Biên (px) bên trong khung nhìn; DIM thò ra ngoài khung bị phạt. */
const VIEWPORT_MARGIN_PX = 6;

/** Phạt khi cạnh đo / đường DIM nằm ở phía khuất sau tủ (xa camera). */
export const HIDDEN_SIDE_PENALTY = 80;

const KINDS = [
  { axis: DIM_AXIS.WIDTH, along: 'z', perp: ['x', 'y'] },
  { axis: DIM_AXIS.HEIGHT, along: 'y', perp: ['x', 'z'] },
  { axis: DIM_AXIS.DEPTH, along: 'x', perp: ['y', 'z'] },
];

// ---- hình học 2D ------------------------------------------------------

/** Bao lồi (Andrew monotone chain) của tập điểm 2D. */
export const convexHull = points => {
  const pts = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  if (pts.length <= 2) return pts;
  const cross = (o, a, b) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const build = list => {
    const out = [];
    list.forEach(p => {
      while (
        out.length >= 2 &&
        cross(out[out.length - 2], out[out.length - 1], p) <= 0
      ) {
        out.pop();
      }
      out.push(p);
    });
    out.pop();
    return out;
  };
  return build(pts).concat(build([...pts].reverse()));
};

/** Điểm nằm trong đa giác lồi (thu nhỏ vào trong `inset` px để bỏ qua viền). */
export const insideHull = (hull, p, inset = 0) => {
  if (hull.length < 3) return false;
  let area = 0;
  hull.forEach((a, i) => {
    const b = hull[(i + 1) % hull.length];
    area += a.x * b.y - b.x * a.y;
  });
  const orient = area >= 0 ? 1 : -1;
  return hull.every((a, i) => {
    const b = hull[(i + 1) % hull.length];
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const dist = ((b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x)) / len;
    return dist * orient >= inset;
  });
};

/** Tỉ lệ các điểm lấy mẫu của đoạn thẳng (a->b, t trong [t0,t1]) nằm trong hull. */
const overlapSamples = (hull, a, b, t0, t1, count) => {
  let inside = 0;
  for (let i = 0; i < count; i += 1) {
    const t = t0 + ((t1 - t0) * (i + 0.5)) / count;
    const p = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    if (insideHull(hull, p, 2)) inside += 1;
  }
  return inside;
};

/**
 * Hướng đẩy `dir` làm đường DIM lùi ra xa camera, tức nằm sau thân vật. Chỉ xét
 * thành phần nằm ngang: đẩy lên / xuống không đưa đường DIM ra sau tủ.
 */
export const pushesAway = (dir, a, b, camera) => {
  const view = a.clone().add(b).multiplyScalar(0.5).sub(camera.position);
  view.y = 0;
  const flat = dir.clone();
  flat.y = 0;
  if (flat.length() < 0.5 || view.lengthSq() < 1e-12) return false;
  return flat.normalize().dot(view.normalize()) > 0.35;
};

// ---- chọn vị trí ----------------------------------------------------

const isVisibleDeep = object => {
  for (let o = object; o; o = o.parent) {
    if (o.visible === false) return false;
  }
  return true;
};

const pushBoxCorners = (box, matrixWorld, camera, width, height, out) => {
  const v = new THREE.Vector3();
  for (const x of [box.min.x, box.max.x]) {
    for (const y of [box.min.y, box.max.y]) {
      for (const z of [box.min.z, box.max.z]) {
        v.set(x, y, z).applyMatrix4(matrixWorld);
        const s = projectToScreen(v, camera, width, height);
        if (!s.visible) return false;
        out.push({ x: s.x, y: s.y });
      }
    }
  }
  return true;
};

/**
 * Hình chiếu (bao lồi) của module trên màn hình, tính từ MỌI mesh đang hiện
 * (thân, cánh, tay nắm, mặt đá…) — không chỉ khung — để DIM né cả những phần
 * thò ra ngoài khung. Không có mesh thì dùng bbox khung.
 * @returns {object[]|null} null nếu có phần nằm sau camera
 */
export const silhouetteOfMeshes = (meshes, camera, width, height) => {
  const pts = [];
  for (const o of meshes) {
    if (!o.isMesh || !o.geometry || !isVisibleDeep(o)) continue;
    if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
    if (o.geometry.boundingBox.isEmpty()) continue;
    o.updateWorldMatrix(true, false);
    if (
      !pushBoxCorners(
        o.geometry.boundingBox,
        o.matrixWorld,
        camera,
        width,
        height,
        pts
      )
    ) {
      return null;
    }
  }
  return pts.length >= 3 ? convexHull(pts) : null;
};

export const silhouetteOf = (group, camera, width, height, frameBox) => {
  group.updateWorldMatrix(true, true);
  const meshes = [];
  group.traverse(o => {
    if (o.isMesh) meshes.push(o);
  });
  const hull = silhouetteOfMeshes(meshes, camera, width, height);
  if (hull || meshes.length) return hull;
  // Không có mesh: dùng bbox khung
  if (!frameBox) return null;
  const pts = [];
  if (
    !pushBoxCorners(frameBox, group.matrixWorld, camera, width, height, pts)
  ) {
    return null;
  }
  return pts.length >= 3 ? convexHull(pts) : null;
};

/**
 * Cạnh của hộp (song song `along`, nằm ở `sideA` của trục `pa` và `sideB` của
 * trục `pb`) có bị thân hộp che khuất không: khuất khi CẢ HAI mặt kề cạnh đó đều
 * quay lưng về camera. Các cạnh này nằm phía sau tủ nên không đặt DIM ở đó.
 */
export const isEdgeHidden = (group, box, pa, sideA, pb, sideB, camera) => {
  const faceBack = (axis, side) => {
    const n = localAxisWorldDir(group, axis).multiplyScalar(
      side === 'max' ? 1 : -1
    );
    const c = box.getCenter(new THREE.Vector3());
    c[axis] = side === 'max' ? box.max[axis] : box.min[axis];
    c.applyMatrix4(group.matrixWorld);
    return n.dot(c.sub(camera.position)) >= 0;
  };
  return faceBack(pa, sideA) && faceBack(pb, sideB);
};

/**
 * Chấm điểm một ứng viên (cạnh + hướng đẩy ra). Điểm càng THẤP càng đẹp.
 *  - phạt nặng khi đường DIM / đường dóng chồng lên thân tủ trên màn hình
 *  - phạt khi DIM bị co ngắn (cạnh gần song song hướng nhìn) hoặc ra ngoài khung
 */
export const scoreCandidate = (
  { p1, p2, q1, q2 },
  hull,
  width,
  height,
  obstacles = []
) => {
  const s = pt => ({ x: pt.x, y: pt.y });
  const [a1, a2, b1, b2] = [p1, p2, q1, q2].map(s);

  const total = 10 + 6 + 6;
  const insideOf = poly =>
    overlapSamples(poly, b1, b2, 0.05, 0.95, 10) +
    overlapSamples(poly, a1, b1, 0.25, 1, 6) +
    overlapSamples(poly, a2, b2, 0.25, 1, 6);
  const inside = insideOf(hull);
  let score = (inside / total) * 100;
  // Đè lên module khác cũng xấu, nhưng nhẹ hơn đè lên chính nó
  const obstacleOverlap = obstacles.length
    ? Math.max(...obstacles.map(poly => insideOf(poly))) / total
    : 0;
  score += obstacleOverlap * 60;

  const lengthPx = Math.hypot(b2.x - b1.x, b2.y - b1.y);
  if (lengthPx < MIN_LENGTH_PX) score += (MIN_LENGTH_PX - lengthPx) * 1.5;

  const out = pt =>
    pt.x < VIEWPORT_MARGIN_PX ||
    pt.y < VIEWPORT_MARGIN_PX ||
    pt.x > width - VIEWPORT_MARGIN_PX ||
    pt.y > height - VIEWPORT_MARGIN_PX;
  if (out(b1) || out(b2)) score += 30;

  return { score, overlap: inside / total, obstacleOverlap, lengthPx };
};

/** Toạ độ local của góc ứng với {x,y,z} = 'min' | 'max'. */
const cornerLocal = (box, sides) =>
  new THREE.Vector3(
    sides.x === 'min' ? box.min.x : box.max.x,
    sides.y === 'min' ? box.min.y : box.max.y,
    sides.z === 'min' ? box.min.z : box.max.z
  );

/**
 * Khoảng đẩy (mét) để đường DIM cách cạnh ~TARGET_OFFSET_PX trên màn hình,
 * kẹp trong [MIN_OFFSET_M, MAX_OFFSET_M].
 */
const offsetMetersFor = (origin, dir, camera, width, height) => {
  const probe = 0.1;
  const a = projectToScreen(origin, camera, width, height);
  const b = projectToScreen(
    origin.clone().addScaledVector(dir, probe),
    camera,
    width,
    height
  );
  const pxPerMeter = Math.hypot(b.x - a.x, b.y - a.y) / probe;
  if (!(pxPerMeter > 1e-6)) return MIN_OFFSET_M * 2.5;
  return Math.max(
    MIN_OFFSET_M,
    Math.min(MAX_OFFSET_M, TARGET_OFFSET_PX / pxPerMeter)
  );
};

/**
 * Với mỗi loại DIM (rộng/cao/sâu) chọn cạnh và hướng đẩy ra đẹp nhất khi nhìn từ
 * camera hiện tại: nằm NGOÀI hình chiếu của module, không bị co ngắn, trong khung.
 * Ứng viên: 4 cạnh song song với trục đo x 2 trục vuông góc để đẩy ra, luôn đẩy
 * ra phía ngoài của hộp (không đẩy vào trong).
 *
 * `kinds`: chỉ tạo các loại DIM này (mặc định cả rộng/cao/sâu).
 * `meshes`: tính hình chiếu từ các mesh này thay vì cả module (dùng cho bộ phận).
 * `obstacles`: group các module khác hoặc mảng mesh của bộ phận khác; DIM tránh đè lên chúng (nhẹ hơn đè lên
 * chính module).
 *
 * @returns {null | {axis, anchors: object[], offsetAxis, offsetSign, offsetM,
 *                   overlap: number, lengthPx: number}[]}
 *   null nếu module nằm (một phần) sau camera
 */
export const chooseAutoPlacements = (
  group,
  box,
  camera,
  width,
  height,
  { obstacles = [], kinds, meshes } = {}
) => {
  if (!group || !box || !camera || !(width > 0) || !(height > 0)) return null;
  // Bộ phận (mặt đá / ốp tường): hình chiếu của chính các mesh của nó
  const hull = meshes
    ? silhouetteOfMeshes(meshes, camera, width, height)
    : silhouetteOf(group, camera, width, height, box);
  if (!hull) return null;
  // Vật cản: group module khác, hoặc mảng mesh của một bộ phận khác
  const obstacleHulls = obstacles
    .filter(o => o && o !== group)
    .map(o =>
      Array.isArray(o)
        ? silhouetteOfMeshes(o, camera, width, height)
        : silhouetteOf(o, camera, width, height)
    )
    .filter(Boolean);
  group.updateWorldMatrix(true, false);

  const wanted = kinds ? KINDS.filter(k => kinds.includes(k.axis)) : KINDS;
  return wanted.map(kind => {
    const [pa, pb] = kind.perp;
    let best = null;

    ['min', 'max'].forEach(sideA =>
      ['min', 'max'].forEach(sideB => {
        const edgeSides = along => ({
          [kind.along]: along,
          [pa]: sideA,
          [pb]: sideB,
        });
        const sidesStart = edgeSides('min');
        const sidesEnd = edgeSides('max');
        const w1 = cornerLocal(box, sidesStart).applyMatrix4(group.matrixWorld);
        const w2 = cornerLocal(box, sidesEnd).applyMatrix4(group.matrixWorld);

        kind.perp.forEach(offsetAxis => {
          const offsetSign = sidesStart[offsetAxis] === 'max' ? 1 : -1;
          const dir = localAxisWorldDir(group, offsetAxis).multiplyScalar(
            offsetSign
          );
          const offsetM = offsetMetersFor(w1, dir, camera, width, height);
          const q1 = w1.clone().addScaledVector(dir, offsetM);
          const q2 = w2.clone().addScaledVector(dir, offsetM);
          const proj = [w1, w2, q1, q2].map(pt =>
            projectToScreen(pt, camera, width, height)
          );
          if (proj.some(pt => !pt.visible)) return;

          // Không đặt DIM ở phía khuất sau tủ: cạnh bị che, hoặc đường DIM bị
          // đẩy ra phía xa camera (nằm sau thân tủ)
          const hidden =
            isEdgeHidden(group, box, pa, sideA, pb, sideB, camera) ||
            pushesAway(dir, w1, w2, camera);

          const result = scoreCandidate(
            { p1: proj[0], p2: proj[1], q1: proj[2], q2: proj[3] },
            hull,
            width,
            height,
            obstacleHulls
          );
          // Hoà điểm: ưu tiên cạnh trên và đẩy lên trên
          const score =
            result.score +
            (hidden ? HIDDEN_SIDE_PENALTY : 0) +
            (sidesStart.y === 'max' || kind.along === 'y' ? 0 : 0.5) +
            (offsetAxis === 'y' ? 0 : 0.2);
          if (best && score >= best.score) return;
          best = {
            score,
            axis: kind.axis,
            anchors: [sidesStart, sidesEnd].map(sides => ({
              key: buildAnchorKey(sides),
            })),
            offsetAxis,
            offsetSign,
            offsetM,
            overlap: result.overlap,
            lengthPx: result.lengthPx,
          };
        });
      })
    );
    return best;
  });
};
